import { useEffect } from 'react';

export interface PageSeoConfig {
  title: string;
  description: string;
  canonicalUrl?: string;
  ogType?: 'website' | 'article';
  ogImage?: string;
  ogImageAlt?: string;
  keywords?: string[];
  author?: string;
  publishedTime?: string;
}

const DEFAULT_ORIGIN = 'https://literafan.com';
const DEFAULT_IMAGE = 'https://literafan.com/vite.svg';
const DEFAULT_TITLE = 'Literafan | Aventura Literária';
const DEFAULT_DESCRIPTION = 'Uma plataforma de aventura literária e comunidade para leitores apaixonados, com resenhas, artigos e recomendações de livros.';

export const setMetaTag = (attrName: string, attrValue: string, content: string) => {
  if (typeof document === 'undefined') return;
  let tag = document.querySelector(`meta[${attrName}="${attrValue}"]`);
  if (!tag) {
    tag = document.createElement('meta');
    tag.setAttribute(attrName, attrValue);
    document.head.appendChild(tag);
  }
  tag.setAttribute('content', content);
};

export const setCanonicalUrl = (url: string) => {
  if (typeof document === 'undefined') return;
  let canonicalLink = document.querySelector('link[rel="canonical"]');
  if (!canonicalLink) {
    canonicalLink = document.createElement('link');
    canonicalLink.setAttribute('rel', 'canonical');
    document.head.appendChild(canonicalLink);
  }
  canonicalLink.setAttribute('href', url);
};

export const updatePageSeo = (config: PageSeoConfig) => {
  if (typeof document === 'undefined') return;

  const fullTitle = config.title.includes('Literafan') 
    ? config.title 
    : `${config.title} | Literafan`;
  
  document.title = fullTitle;

  const description = config.description || DEFAULT_DESCRIPTION;
  const canonicalUrl = config.canonicalUrl || (typeof window !== 'undefined' ? `${window.location.origin}${window.location.pathname}` : DEFAULT_ORIGIN);
  const ogImage = config.ogImage || DEFAULT_IMAGE;
  const ogType = config.ogType || 'website';

  // 1. Meta Básicas
  setMetaTag('name', 'description', description);
  setMetaTag('name', 'robots', 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1');
  
  if (config.keywords && config.keywords.length > 0) {
    setMetaTag('name', 'keywords', config.keywords.join(', '));
  }

  if (config.author) {
    setMetaTag('name', 'author', config.author);
  }

  // 2. Canonical URL
  setCanonicalUrl(canonicalUrl);

  // 3. Open Graph
  setMetaTag('property', 'og:title', fullTitle);
  setMetaTag('property', 'og:description', description);
  setMetaTag('property', 'og:url', canonicalUrl);
  setMetaTag('property', 'og:type', ogType);
  setMetaTag('property', 'og:site_name', 'Literafan');
  setMetaTag('property', 'og:locale', 'pt_BR');
  setMetaTag('property', 'og:image', ogImage);

  if (config.ogImageAlt) {
    setMetaTag('property', 'og:image:alt', config.ogImageAlt);
  }

  if (config.publishedTime) {
    setMetaTag('property', 'article:published_time', config.publishedTime);
  }

  if (config.author && ogType === 'article') {
    setMetaTag('property', 'article:author', config.author);
  }

  // 4. Twitter Cards
  setMetaTag('name', 'twitter:card', 'summary_large_image');
  setMetaTag('name', 'twitter:title', fullTitle);
  setMetaTag('name', 'twitter:description', description);
  setMetaTag('name', 'twitter:image', ogImage);

  if (config.ogImageAlt) {
    setMetaTag('name', 'twitter:image:alt', config.ogImageAlt);
  }
};

export const usePageSeo = (config: PageSeoConfig) => {
  useEffect(() => {
    updatePageSeo(config);
  }, [
    config.title,
    config.description,
    config.canonicalUrl,
    config.ogType,
    config.ogImage,
    config.ogImageAlt,
    config.author,
    config.publishedTime
  ]);
};
